"use client";

import { useState } from "react";
import dynamic from "next/dynamic";
import { Check, Copy } from "@/components/icons";

const CodeBlockPrism = dynamic(() => import("./code-block-prism"), {
  ssr: false,
  loading: () => (
    <pre
      aria-hidden="true"
      style={{
        margin: 0,
        background: "transparent",
        padding: "1.25rem 1.5rem",
        fontSize: "0.8125rem",
        lineHeight: 1.7,
        color: "rgba(255,255,255,0.7)",
        fontFamily: "var(--font-mono, ui-monospace, monospace)",
        whiteSpace: "pre-wrap",
      }}
    />
  ),
});

function detectLanguage(code: string): string {
  const c = code.trimStart();

  if (/^<\?php/i.test(c) || /\$[a-zA-Z_]\w*\s*=/.test(c)) return "php";
  if (
    /^#!/.test(c) ||
    /\b(grep|curl|sudo|apt|brew|chmod|pip|npm|yarn|pnpm|python|node|git|docker|kubectl|wget|ssh|cp|mv|rm|mkdir|cd|ls|cat|export|source)\b/.test(
      c
    )
  )
    return "bash";
  // .env files: lines like KEY=VALUE with no spaces around =
  if (/^[A-Z][A-Z0-9_]+=\S/m.test(c) && !/\bdef\b|\bfunction\b/.test(c)) return "bash";
  // YAML: top-level key: with nested indented keys
  if (/^[\w-]+:\s*$/m.test(c) || (/^[\w-]+:\s*\S/m.test(c) && /^\s{2,}[\w-]+:/m.test(c)))
    return "yaml";
  // SQL: require actual query structure, not just the word "from"
  if (
    /\bSELECT\b[\s\S]+?\bFROM\b|\bINSERT\s+INTO\b|\bUPDATE\b[\s\S]+?\bSET\b|\bDELETE\s+FROM\b/i.test(
      c
    )
  )
    return "sql";
  if (/<\s*(html|head|body|div|span|p|a|script|style)[\s>]/i.test(c)) return "html";
  if (/^\s*[\[{]/.test(c) && /"\w+":\s/.test(c)) return "json";
  // Python before JS to prevent from/import confusion
  // bare "import x" or "import x as y" with no { or quotes = Python module import
  if (
    /\bdef\s+\w+\s*\(|\bfrom\s+[\w.]+\s+import\b|\belif\b|\bprint\s*\(|^\s*class\s+\w+:|^import\s+[a-zA-Z_][\w.]*(\s+as\s+\w+)?\s*$/m.test(
      c
    )
  )
    return "python";
  if (/\bfunc\s+\w+|:=|\bpackage\s+main\b/.test(c)) return "go";
  if (/\bpublic\s+class\b|\bSystem\.out\.print/.test(c)) return "java";
  if (/\busing\s+System|\bnamespace\s+\w|\bConsole\.Write/.test(c)) return "csharp";
  if (/\bfn\s+\w+\s*\(|\blet\s+mut\b|\bimpl\b/.test(c)) return "rust";
  if (/\brequire\s*['"]|\bmodule\.exports\b/.test(c)) return "javascript";
  if (/\b(const|let|var)\b.+=|=>|\bimport\s+{/.test(c)) return "javascript";
  if (/^\s*[.#][\w-]+\s*\{|:\s*(px|em|rem|%|auto|flex|grid)\b/.test(c)) return "css";
  return "text";
}

export default function CodeBlock({ code, lang }: { code: string; lang?: string }) {
  const [copied, setCopied] = useState(false);
  const resolvedLang = lang ?? detectLanguage(code);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      className="my-6 overflow-hidden rounded-xl border"
      style={{ borderColor: "rgba(255,255,255,0.05)", background: "#0d0d0f" }}
    >
      <div
        className="flex items-center justify-between border-b px-5 py-3"
        style={{ borderColor: "rgba(255,255,255,0.04)" }}
      >
        {/* The code block keeps its dark editor chrome in both themes (like a
            screenshot), so these colors are fixed rather than token-based. */}
        <span className="rounded bg-white/4 px-2 py-0.5 font-mono text-xs font-medium text-[#a0a0a0]">
          {resolvedLang}
        </span>
        <button
          type="button"
          onClick={handleCopy}
          className="flex items-center gap-1.5 text-xs text-[#a0a0a0] transition-colors hover:text-white"
          aria-label="Copy code"
        >
          {copied ? <Check size={12} /> : <Copy size={12} />}
          {copied ? "Copied!" : "Copy"}
        </button>
      </div>
      <CodeBlockPrism code={code} language={resolvedLang} />
    </div>
  );
}
